import { useState, useEffect } from 'react';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { usePublishQuickNoteContext } from '../../notebook/quickNoteContext';
import { useApi } from '../../hooks/useApi';
import { Btn, Modal, Spinner, Badge, StatusBadge, Icon, clickable } from '../ui';
import { NotesFeed } from './Notes';
import { TaskEditModal } from './TaskEditModal';
import { EDITABLE_STATUSES, STATUS_COLORS, Avatar } from '../tasks/TaskComponents';

const labelStyle = { fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8, display: 'block' };
const factLabel  = { fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 5 };
const factValue  = { fontSize: 14, fontWeight: 600, color: B.text };

// A person value: avatar + name, or a muted "Unassigned" placeholder.
const Who = ({ name }) => name
  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Avatar name={name} size={22} />{name}</span>
  : <span style={{ color: B.textLt, fontStyle: 'italic', fontWeight: 500 }}>Unassigned</span>;

// Due-date history — a hover/focus tooltip next to the Due Date label. Reads the
// per-task change trail from the temporal history (GET tasks/{id}/due-date-history)
// and only renders the clock icon when there's an actual history to show.
const DueDateHistoryTip = ({ taskId }) => {
  const { data } = useApi(() => api.get(`tasks/${taskId}/due-date-history`), [taskId]);
  const [show, setShow] = useState(false);
  const history = data || [];
  if (!history.length) return null;
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <button aria-label="Due date history" onFocus={() => setShow(true)} onBlur={() => setShow(false)}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${B.border}`, background: B.white, color: B.textMd, cursor: 'help', padding: 0 }}>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      </button>
      {show && (
        <span style={{ position: 'absolute', top: 'calc(100% + 7px)', left: -8, zIndex: 40, width: 250, background: B.white, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, padding: '10px 12px', textTransform: 'none', letterSpacing: 0 }}>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>Due date history</span>
          {history.map((h, i) => (
            <span key={i} style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '5px 0', borderTop: i ? `1px solid ${B.border}` : 'none' }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: B.text }}>
                {h.fromDate
                  ? <>{fmt.date(h.fromDate)} <span style={{ color: B.textLt }}>→</span> <span style={{ color: B.navy }}>{fmt.date(h.toDate)}</span></>
                  : h.toDate ? <>Set to {fmt.date(h.toDate)}</> : <>Cleared</>}
              </span>
              <span style={{ fontSize: 11, color: B.textLt }}>{fmt.date(h.changedUTC)} · {h.changedByName}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

// Full-width status dropdown for the modal rail — the same behavior as
// InlineStatusSelect (in TaskComponents), styled as a bordered select rather
// than a dense table pill. Colors come from the shared STATUS_COLORS map.
const StatusDropdown = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const cur = status || 'Not Started';
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} onBlur={() => setTimeout(() => setOpen(false), 120)} aria-haspopup="listbox" aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 12px', background: B.white, border: `1px solid ${B.border}`, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: B.text }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_COLORS[cur], flexShrink: 0 }} />
        {cur}
        <span style={{ marginLeft: 'auto', color: B.textMd, display: 'inline-flex' }}><Icon name="chevronDown" size={13} /></span>
      </button>
      {open && (
        <div role="listbox" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: B.white, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, overflow: 'hidden' }}>
          {EDITABLE_STATUSES.map(s => (
            <button key={s} role="option" aria-selected={s === cur}
              onMouseDown={() => { setOpen(false); if (s !== cur) onChange(s); }}
              style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 12px', border: 'none', background: s === cur ? B.surfaceMuted : B.white, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: B.text, textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = s === cur ? B.surfaceMuted : B.white}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_COLORS[s], flexShrink: 0 }} />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Shared task detail modal — the full read/act view shown when you click a task
// on the Tasks table or the Dashboard. Self-fetches the task (detail + messages)
// so callers only pass the id. `onChanged` fires after any mutation (status,
// edit, delete) so the caller can refresh its own list.
//
// Layout: a two-column body — a left "facts" rail (priority, due date + history,
// deal, status) and a right activity column (assignees + a Notes / Messages /
// Deal Notes tab panel). Collapses to one column on narrow screens.
//
// Props:
//   taskId    — task to view
//   users     — user list for @mentions and the edit picker
//   onClose   — close the modal
//   onChanged — called after a status change / edit / delete
export const TaskDetailModal = ({ taskId, users = [], onClose, onChanged, targetNoteId = null, onNoteTargetHandled }) => {
  const { data: task, loading, reload } = useApi(() => api.get(`tasks/${taskId}`), [taskId]);
  // Quick Note offers to link a note to the task on screen.
  usePublishQuickNoteContext(taskId ? { type: 'Task', id: taskId, label: task?.title } : null);
  const [msgs, setMsgs]                         = useState([]);
  const [newMsg, setNewMsg]                     = useState('');
  const [mentionedUserIDs, setMentionedUserIDs] = useState([]);
  const [mentionQuery, setMentionQuery]         = useState(null);
  const [showEdit, setShowEdit]                 = useState(false);
  const [activeTab, setActiveTab]               = useState('Notes');

  useEffect(() => { setMsgs(task?.messages || []); }, [task]);
  // A note deep-link (from global search) targets this task's own notes — make
  // sure the Notes tab is showing so the feed can scroll to it.
  useEffect(() => { if (targetNoteId) setActiveTab('Notes'); }, [targetNoteId]);

  const changeStatus = async (status) => {
    try { await api.put(`tasks/${taskId}/status`, { status }); await reload(); onChanged?.(); }
    catch {}
  };

  const [completing, setCompleting] = useState(false);
  const completeTask = async () => {
    setCompleting(true);
    try { await changeStatus('Complete'); } finally { setCompleting(false); }
  };

  const onMsgChange = (e) => {
    const val = e.target.value;
    setNewMsg(val);
    const lastAt = val.lastIndexOf('@');
    if (lastAt >= 0 && lastAt === val.length - 1) {
      setMentionQuery('');
    } else if (lastAt >= 0 && !val.slice(lastAt + 1).includes(' ')) {
      setMentionQuery(val.slice(lastAt + 1).toLowerCase());
    } else {
      setMentionQuery(null);
    }
  };

  const pickMention = (user) => {
    const lastAt = newMsg.lastIndexOf('@');
    setNewMsg(`${newMsg.slice(0, lastAt)}@${user.displayName} `);
    setMentionedUserIDs(p => p.includes(user.userID) ? p : [...p, user.userID]);
    setMentionQuery(null);
  };

  const sendMsg = async () => {
    if (!newMsg.trim()) return;
    try {
      const m = await api.post(`tasks/${taskId}/messages`, { content: newMsg.trim(), mentionedUserIDs });
      setMsgs(p => [...p, m]);
      setNewMsg(''); setMentionedUserIDs([]); setMentionQuery(null);
    } catch {}
  };

  const assignees = task?.assignees || [];
  const assignedNames = assignees
    .map(a => a.displayName || a.contactName)
    .filter(Boolean)
    .join(', ');
  // A task can be assigned BY multiple people. Prefer the full assignor set,
  // falling back to the singular assignedByName for un-backfilled tasks.
  const assignors = task?.assignors || [];
  const assignedByNames = assignors.length
    ? assignors.map(a => a.displayName).filter(Boolean).join(', ')
    : (task?.assignedByName || '');
  // Show the immutable original assignor only when they are no longer among the
  // current assignors (i.e. the task was reassigned away from its creator).
  const showOriginalAssignor = task?.originalAssignedByName && (
    assignors.length
      ? !assignors.some(a => a.userID === task.originalAssignedByUserID)
      : task.originalAssignedByUserID !== task.assignedByUserID
  );
  // Outside parties — external contacts associated with the task (not assignees).
  const outsidePartyNames = (task?.outsideParties || [])
    .map(p => p.contactName)
    .filter(Boolean)
    .join(', ');
  const overdue = task?.dueDate && !task?.isCompleted && new Date(task.dueDate) < new Date();
  const userMatches = mentionQuery === null ? [] : (users || []).filter(u => u.displayName?.toLowerCase().includes(mentionQuery));

  // A task's conversation is its notes (migration 236 copied every message
  // into a task note). The Messages tab survives only for messages not copied
  // yet — on a database behind 236 — so nothing already written disappears.
  const tabs = ['Notes', ...(msgs.length ? ['Messages'] : []), ...(task?.dealID ? ['Deal Notes'] : [])];

  return (
    <>
      <Modal title={task?.title || 'Task'} onClose={onClose} width={820}
        headerAction={task ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* One-click completion. Not offered on a group heading: a parent with
                subtasks has no status of its own — it is done when they are. */}
            {!task.isCompleted && !(task.children || []).length && (
              <Btn size="sm" onClick={completeTask} disabled={completing}>
                {completing ? 'Completing…' : 'Mark complete'}
              </Btn>
            )}
            <Btn size="sm" variant="secondary" onClick={() => setShowEdit(true)}>Edit</Btn>
          </div>
        ) : null}>
        {(loading || !task) ? <Spinner /> : (
          <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 22, alignItems: 'start' }}>

            {/* LEFT RAIL — the facts */}
            <aside style={{ background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <StatusBadge status={task.priority} />
                {!!task.isBlocked && <Badge color={B.amber}>Blocked</Badge>}
                {task.isCompleted && <Badge color={B.green}>Completed</Badge>}
                {task.sourceNoteID && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: B.textMd, padding: '2px 8px', background: B.white, borderRadius: 12, border: `1px solid ${B.border}` }}>
                    <Icon name="note" size={12} /> From a note
                  </span>
                )}
              </div>

              <div style={{ height: 1, background: B.border }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' }}>
                <div>
                  <div style={factLabel}>Due Date<DueDateHistoryTip taskId={task.taskID} /></div>
                  <div style={factValue}>
                    {overdue
                      ? <span style={{ color: B.red, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="warning" size={13} /> {fmt.date(task.dueDate)}</span>
                      : (task.dueDate ? fmt.date(task.dueDate) : <span style={{ color: B.textLt, fontWeight: 500 }}>—</span>)}
                  </div>
                </div>
                <div>
                  <div style={factLabel}>Deal</div>
                  <div style={factValue}>{task.dealTitle || <span style={{ color: B.textLt, fontWeight: 500 }}>—</span>}</div>
                </div>
                {/* Project is an ORTHOGONAL TAG (see ProjectPicker.jsx), not a
                    scope — shown only when the task carries one, and only as
                    a fact here; changing it is a TaskEditModal concern. */}
                {task.projectTitle && (
                  <div>
                    <div style={factLabel}>Project</div>
                    <div style={factValue}>{task.projectTitle}</div>
                  </div>
                )}
              </div>

              <div style={{ height: 1, background: B.border }} />

              <div>
                <label style={labelStyle}>Status</label>
                <StatusDropdown status={task.status} onChange={changeStatus} />
              </div>
            </aside>

            {/* RIGHT COLUMN — activity */}
            <section style={{ minWidth: 0 }}>
              <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, paddingBottom: 14, borderBottom: `1px solid ${B.border}` }}>
                <div>
                  <div style={factLabel}>Assigned To</div>
                  <div style={factValue}><Who name={assignedNames} /></div>
                </div>
                <div>
                  <div style={factLabel}>Assigned By</div>
                  <div style={factValue}><Who name={assignedByNames} /></div>
                </div>
                {/* Original assignor (creator) is immutable; surface it only when the task was later reassigned. */}
                {showOriginalAssignor && (
                  <div>
                    <div style={factLabel}>Originally Assigned By</div>
                    <div style={factValue}><Who name={task.originalAssignedByName} /></div>
                  </div>
                )}
                {task.isCompleted && task.completedByName && (
                  <div>
                    <div style={factLabel}>Completed By</div>
                    <div style={factValue}><Who name={task.completedByName} /></div>
                  </div>
                )}
                {outsidePartyNames && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={factLabel}>Outside Parties</div>
                    <div style={factValue}>{outsidePartyNames}</div>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${B.border}`, marginTop: 14, marginBottom: 14 }}>
                {tabs.map(t => {
                  const active = activeTab === t;
                  const count = t === 'Messages' ? msgs.length : 0;
                  return (
                    <button key={t} role="tab" aria-selected={active} onClick={() => setActiveTab(t)}
                      style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: active ? B.navy : B.textMd, padding: '9px 4px', marginRight: 20, marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 7, borderBottom: active ? `2px solid ${B.navy}` : '2px solid transparent' }}>
                      {t}
                      {count > 0 && <span style={{ fontSize: 10.5, fontWeight: 700, background: active ? B.navy : B.surfaceSunken, color: active ? '#fff' : B.textMd, borderRadius: 20, padding: '0 7px', lineHeight: '17px', minWidth: 17, textAlign: 'center' }}>{count}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Panels */}
              {activeTab === 'Notes' && <NotesFeed taskId={task.taskID} users={users} targetNoteId={targetNoteId} onNoteTargetHandled={onNoteTargetHandled} />}

              {activeTab === 'Messages' && (
                <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, overflow: 'visible' }}>
                  <div style={{ maxHeight: 260, overflowY: 'auto', padding: msgs.length ? 12 : 0 }}>
                    {msgs.length === 0
                      ? <div style={{ fontSize: 13, color: B.textLt, textAlign: 'center', padding: '28px 16px' }}>No messages yet. Start the conversation below.</div>
                      : msgs.map((m, i) => (
                        <div key={m.messageID ?? i} style={{ marginBottom: i === msgs.length - 1 ? 0 : 10, padding: '8px 12px', borderRadius: 8, background: B.surfaceMuted }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                            <Avatar name={m.sentByName} size={20} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: B.text }}>{m.sentByName}</span>
                            <span style={{ fontSize: 11, color: B.textLt }}>{fmt.date(m.sentUTC)}</span>
                          </div>
                          <div style={{ fontSize: 13, color: B.text, whiteSpace: 'pre-wrap', paddingLeft: 28 }}>{m.content}</div>
                        </div>
                      ))}
                  </div>
                  {mentionQuery !== null && (
                    <div style={{ borderTop: `1px solid ${B.border}`, maxHeight: 160, overflowY: 'auto', background: B.white }}>
                      <div style={{ padding: '4px 12px', fontSize: 11, color: B.textLt, fontWeight: 600, letterSpacing: 0.5, borderBottom: `1px solid ${B.border}` }}>MENTION A USER</div>
                      {userMatches.map(u => (
                        <div key={u.userID} {...clickable(() => pickMention(u), { role: 'option' })}
                          style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted} onMouseLeave={e => e.currentTarget.style.background = B.white}>
                          <Avatar name={u.displayName} size={22} />
                          <span>{u.displayName}</span>
                        </div>
                      ))}
                      {userMatches.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: B.textLt }}>No users found</div>}
                    </div>
                  )}
                  <div style={{ borderTop: `1px solid ${B.border}`, display: 'flex', gap: 8, padding: 10, alignItems: 'center' }}>
                    <input value={newMsg} onChange={onMsgChange} placeholder="Type a message… use @ to mention someone"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) { e.preventDefault(); sendMsg(); } if (e.key === 'Escape') setMentionQuery(null); }}
                      style={{ flex: 1, padding: '8px 12px', border: `1px solid ${B.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
                    <Btn size="sm" onClick={sendMsg} disabled={!newMsg.trim()}>Send</Btn>
                  </div>
                </div>
              )}

              {activeTab === 'Deal Notes' && (
                <div>
                  <div style={{ fontSize: 12, color: B.textLt, marginBottom: 12 }}>
                    Read-only notes from deal <b style={{ color: B.textMd }}>{task.dealTitle}</b>.
                  </div>
                  <NotesFeed dealId={task.dealID} dealDetailId={task.dealDetailID || undefined} includeDealLevel readOnly users={users} />
                </div>
              )}
            </section>
          </div>
        )}
      </Modal>

      {showEdit && task && (
        <TaskEditModal taskId={taskId} users={users}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onChanged?.(); onClose?.(); }} />
      )}
    </>
  );
};
